import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useHistory } from 'react-router-dom';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import PrivacyModal from 'components/modal/PrivacyModal';
import TermsAndConditionsModal from 'components/modal/TermsAndConditionsModal';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { inquirySubmit } from 'libs/api/main';
import { filteredFileNames } from 'libs/utils';
import { TInquiry } from 'modules/main';
import { CWindowWidth, CEmail, CUploadFileProps } from 'constants/base.const';
import { ECustomerInquiryCategory, ENotificationType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Tabs, Col, Row, Typography, Form, Button, Input, Radio, Upload, message } from 'antd';
import styled from 'styled-components';
import { FormWrapper, PrimaryColor } from 'GlobalStyles';
import BackgroundSource from 'assets/images/banners/8.webp';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const CommonWrapper = styled.div`
  .title_wrapper {
    font-size: 17px;
    width: 100%;
    height: 300px;
    background-image: url(${BackgroundSource});
    background-size: cover;
    padding: 100px 10px;
    .ant-typography {
      width: 100%;
      max-width: ${CWindowWidth - 300}px;
      color: #ffffff;
      margin: auto;
      display: block;
    }
  }
`;
const IntroductionCustomerInquiryWrapper = styled.div`
  padding: 0px 10px;
  .ant-typography {
    color: #000;
  }
`;
const IntroductionCustomerInquiry = () => {
  const location = useLocation();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [form] = Form.useForm();
  const [key, setKey] = useState('');
  const [category, setCategory] = useState(ECustomerInquiryCategory.Mentoring);
  const history = useHistory();
  const [privacyModal, setPrivacyModal] = useState(false);
  const [termsAndConditionsModal, setTermsAndConditionsModal] = useState(false);
  const [uploadVisible, setUploadVisible] = useState(true);

  const onClickSubmit = () => {
    form
      .validateFields()
      .then((value) => {
        handleSubmit(value as TInquiry);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  };

  const handleSubmit = async (value: TInquiry) => {
    try {
      setSubmitButtonDisabled(true);
      const response = await inquirySubmit({
        category: value.category,
        title: value.title,
        fullname: value.fullname,
        email: value.email,
        phone: value.phone,
        university_name: value.university_name,
        company_name: value.company_name,
        available_datetime: value.available_datetime,
        body: value.body,
        files_ids: filteredFileNames(value.resume_files),
      });
      history.push({
        pathname: '/',
        state: {
          notification: {
            type: ENotificationType.Success,
            message: '요청을 완료했습니다. 빠른 시일내로 답변드리겠습니다.',
          },
        },
      });
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  };

  useEffect(() => {
    setKey(location.pathname);
  }, [location]);

  return (
    <CommonWrapper>
      <Helmet>
        <title>문의하기</title>
      </Helmet>
      <div className='title_wrapper'>
        <Title>문의하기</Title>
        <Text style={{ marginTop: '10px', fontSize: '16px' }}>
          {' '}
          Career Step 에 대해 궁금한 사항을 문의하세요.{' '}
        </Text>
      </div>
      <Tabs
        activeKey={key}
        onChange={(e) => {
          setKey(e);
        }}
        centered={true}
      >
        <TabPane tab='문의하기' key={ERoute.IntroductionCustomerInquiry}>
          <IntroductionCustomerInquiryWrapper>
            <Row
              style={{
                maxWidth: `${CWindowWidth - 300}px`,
                margin: '20px auto',
                padding: '0px',
              }}
              gutter={[0, 16]}
            >
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Title style={{ marginBottom: '30px' }} level={3}>
                  궁금하신 사항이 있으신가요?
                  <br />
                  언제든 문의해 주세요.
                </Title>
                <div style={{ marginBottom: '5px' }}>
                  <Text strong>근무시간</Text>
                </div>
                <div>
                  <Text>
                    월요일~금요일(공휴일 제외)
                    <br />
                    오전 9시 ~ 오후 6시
                    <br />
                  </Text>
                </div>
                <div style={{ marginBottom: '5px', marginTop: '20px' }}>
                  <Text strong>이메일</Text>
                </div>
                <div>
                  <Text>
                    <a style={{ color: '#000' }} href={`mailto:${CEmail}`}>
                      {CEmail}
                    </a>
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                <FormWrapper style={{ maxWidth: '489px' }}>
                  <Form
                    form={form}
                    name='Inquiry'
                    layout='vertical'
                    initialValues={{
                      category: ECustomerInquiryCategory.Mentoring,
                    }}
                    className='form-normal'
                  >
                    <Form.Item
                      name={'category'}
                      label='카테고리'
                      className='form-item form-item-fit-content'
                      rules={[{ required: true }]}
                    >
                      <Radio.Group
                        className='col-12'
                        onChange={() => setCategory(form.getFieldValue('category'))}
                      >
                        <Radio.Button value={ECustomerInquiryCategory.Mentoring}>
                          서비스 이용 상담
                        </Radio.Button>
                        <Radio.Button value={ECustomerInquiryCategory.General}>기타 일반 문의</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item name={'title'} className='form-item' label='제목' rules={[{ required: true }]}>
                      <Input size='large' placeholder='제목을 입력해 주세요' />
                    </Form.Item>
                    <Form.Item
                      name={'fullname'}
                      className='form-item'
                      label='이름'
                      rules={[{ required: true }]}
                    >
                      <Input size='large' placeholder='이름을 입력해 주세요' />
                    </Form.Item>
                    <Form.Item
                      name={'email'}
                      className='form-item'
                      label='이메일'
                      rules={[
                        {
                          type: 'email',
                          message: '이메일 형식이 올바르지 않습니다.',
                        },
                        { required: true },
                      ]}
                    >
                      <Input size='large' placeholder='이메일을 입력해 주세요' />
                    </Form.Item>
                    <Form.Item
                      name={'phone'}
                      className='form-item'
                      label='휴대폰 번호'
                      rules={[{ required: true }]}
                      getValueFromEvent={(event) => {
                        return event.target.value.replace(/[^\d]/g, '');
                      }}
                    >
                      <Input size='large' placeholder='휴대폰 번호를 입력해 주세요' />
                    </Form.Item>
                    <Form.Item
                      name={'university_name'}
                      className='form-item'
                      label='학교명'
                      rules={[{ required: true }]}
                    >
                      <Input size='large' placeholder='학교이름을 입력해 주세요' />
                    </Form.Item>
                    <Form.Item name={'company_name'} className='form-item' label='회사명'>
                      <Input size='large' placeholder='회사 근무 시 입력해 주세요' />
                    </Form.Item>
                    {category === ECustomerInquiryCategory.Mentoring && (
                      <Form.Item
                        name={'available_datetime'}
                        className='form-item'
                        label='상담 가능 날짜 및 시간 (zoom으로 진행합니다)'
                        rules={[{ required: true }]}
                      >
                        <Input size='large' placeholder='상담 가능한 날짜 및 시간 2-3개 옵션을 남겨주세요' />
                      </Form.Item>
                    )}
                    <Form.Item name={'body'} className='form-item' label='내용' rules={[{ required: true }]}>
                      <Input.TextArea
                        placeholder='내용을 입력해 주세요'
                        style={{ resize: 'none' }}
                        rows={8}
                      />
                    </Form.Item>

                    <Form.Item
                      name={'resume_files'}
                      className='form-item'
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.resume_files !== currentValues.resume_files
                      }
                      valuePropName='fileList'
                      label='이력서 업로드'
                      getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
                    >
                      <Upload
                        name='file'
                        {...CUploadFileProps}
                        action={`${Configs.API_HOST}/main/file`}
                        multiple={true}
                        onChange={(info: UploadChangeParam) => {
                          const { status } = info.file;

                          if (status === 'done') {
                            message.success(`${info.file.name} 업로드에 성공했습니다.`);
                            // setUploadVisible(false); //'업로드'버튼 없애기
                          } else if (status === 'error') {
                            message.error(`${info.file.name} 업로드에 실패했습니다.`);
                          } else if (status === 'removed') {
                            if (info.fileList.length === 0) {
                              // setUploadVisible(true);  //'업로드'버튼 보이기
                            }
                          }
                        }}
                      >
                        {/* {uploadVisible === true && ( */}
                        <div style={{ float: 'right' }} className='input-suffix'>
                          업로드
                        </div>
                        {/* )} */}
                      </Upload>
                    </Form.Item>

                    <div className='form-bottom'>
                      <Form.Item style={{ float: 'left' }}>
                        <span
                          style={{ cursor: 'pointer', color: PrimaryColor }}
                          onClick={() => {
                            setPrivacyModal(true);
                          }}
                        >
                          개인정보 보호정책
                        </span>
                        과{' '}
                        <span
                          style={{ cursor: 'pointer', color: PrimaryColor }}
                          onClick={() => {
                            setTermsAndConditionsModal(true);
                          }}
                        >
                          서비스약관
                        </span>
                        이 적용됩니다.
                      </Form.Item>
                      <Form.Item style={{ float: 'right' }}>
                        <Button
                          size='large'
                          type='primary'
                          htmlType='submit'
                          onClick={onClickSubmit}
                          loading={submitButtonDisabled}
                        >
                          제출
                        </Button>
                      </Form.Item>
                    </div>
                  </Form>
                </FormWrapper>
              </Col>
            </Row>
          </IntroductionCustomerInquiryWrapper>
        </TabPane>
      </Tabs>
      <PrivacyModal visible={privacyModal} setVisible={setPrivacyModal} />
      <TermsAndConditionsModal visible={termsAndConditionsModal} setVisible={setTermsAndConditionsModal} />
    </CommonWrapper>
  );
};

export default IntroductionCustomerInquiry;
